import { useMemo, useState } from 'react';
import { Check, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useUpdateWorkAssignmentListAttachment } from 'components/IAP/hooks/assignmentListHooks';
import {
  useCreateWorkAssignment,
  useUpdateWorkAssignment,
  useWorkAssignments,
  type WorkAssignment,
} from 'components/IAP/hooks/workAssignmentHooks';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import {
  ICS204_DELETE_BTN,
  ICS204_HINT,
  ICS204_ICON_BTN,
  ICS204_MUTED,
  ICS204_OUTLINE_BTN,
  ICS204_SUBCARD,
  ICS204_TIPTAP,
} from './assignmentCardStyles';
import { Ics204DisabledActionTooltip } from './Ics204DisabledActionTooltip';
import { Ics204RichTextView } from './Ics204Fields';
import { ics204HtmlToPlainText, isIcs204RichTextEmpty } from './ics204RichText';

type WorkAssignmentsSectionProps = {
  workGroupId: string;
  periodNumber: number;
  walaId: string | null;
  canEdit: boolean;
  disabledReason?: string | null;
};

type EditingState =
  | { kind: 'new'; name: string }
  | { kind: 'existing'; id: string; name: string }
  | null;

/**
 * Box 6 on the tip path: persistent Add + per-row Check/Cancel (immediate API).
 * No section-level edit chrome.
 */
const WorkAssignmentsSection = ({
  workGroupId,
  periodNumber,
  walaId,
  canEdit,
  disabledReason = null,
}: WorkAssignmentsSectionProps) => {
  const { data: allWorkAssignments, isPending } = useWorkAssignments();
  const { mutateAsync: createWorkAssignment } = useCreateWorkAssignment();
  const { mutateAsync: updateWorkAssignment } = useUpdateWorkAssignment();
  const { mutateAsync: updateWala } = useUpdateWorkAssignmentListAttachment();
  const [editing, setEditing] = useState<EditingState>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const rows = useMemo(
    () =>
      (allWorkAssignments ?? []).filter(
        (wa) =>
          wa.work_group_id === workGroupId &&
          wa.operational_period_number === periodNumber &&
          !wa.archived
      ),
    [allWorkAssignments, workGroupId, periodNumber]
  );

  const serverById = useMemo(() => {
    const map = new Map<string, WorkAssignment>();
    for (const wa of rows) map.set(wa.id, wa);
    return map;
  }, [rows]);

  const touchTip = async () => {
    if (!walaId) return;
    await updateWala({ walaId, touch: true });
  };

  const startAdd = () => {
    if (!canEdit || isSaving) return;
    setNameError(null);
    setEditing({ kind: 'new', name: '' });
  };

  const startEdit = (wa: WorkAssignment) => {
    if (!canEdit || isSaving) return;
    setNameError(null);
    setEditing({ kind: 'existing', id: wa.id, name: wa.work_assignment ?? '' });
  };

  const cancelEdit = () => {
    setNameError(null);
    setEditing(null);
  };

  const confirmEdit = async () => {
    if (!editing) return;
    const name = editing.name;
    const plain = ics204HtmlToPlainText(name);
    if (isIcs204RichTextEmpty(name)) {
      setNameError('Work assignment name is required');
      return;
    }
    if (plain.length > 255) {
      setNameError('This maximum length is 255 characters');
      return;
    }
    setIsSaving(true);
    try {
      if (editing.kind === 'new') {
        await createWorkAssignment({
          work_assignment: name,
          status: 'Planned',
          parent_tactics: [],
          work_group_id: workGroupId,
          operational_period_number: periodNumber,
        });
      } else {
        const server = serverById.get(editing.id);
        if (!server) return;
        if ((server.work_assignment ?? '') !== name) {
          await updateWorkAssignment({
            ...server,
            id: server.id,
            work_assignment: name,
          });
        }
      }
      await touchTip();
      setEditing(null);
      setNameError(null);
    } catch {
      // Mutation hooks toast; keep the row open for retry.
    } finally {
      setIsSaving(false);
    }
  };

  const archiveRow = async (wa: WorkAssignment) => {
    if (!canEdit || isSaving) return;
    setIsSaving(true);
    try {
      await updateWorkAssignment({
        ...wa,
        id: wa.id,
        archived: true,
      });
      await touchTip();
      if (editing?.kind === 'existing' && editing.id === wa.id) {
        setEditing(null);
      }
    } catch {
      // Mutation hooks toast.
    } finally {
      setIsSaving(false);
    }
  };

  if (isPending) {
    return <div className={ICS204_MUTED}>Loading work assignments…</div>;
  }

  const editingExistingId = editing?.kind === 'existing' ? editing.id : null;
  const showEmpty = rows.length === 0 && editing?.kind !== 'new';

  return (
    <div data-testid="ics204-edit-work-assignments">
      <p className={ICS204_HINT}>
        Work assignments are shared with the ICS-215. Changes here appear there
        as well.
      </p>
      {(() => {
        const addButton = (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(ICS204_OUTLINE_BTN, 'tw:mt-2')}
            data-testid="ics204-edit-add-work-assignment"
            disabled={!canEdit || isSaving || editing?.kind === 'new'}
            onClick={startAdd}
          >
            <Plus className="tw:h-4 tw:w-4" />
            Add Work Assignment
          </Button>
        );
        return !canEdit && disabledReason ? (
          <Ics204DisabledActionTooltip reason={disabledReason}>
            {addButton}
          </Ics204DisabledActionTooltip>
        ) : (
          addButton
        );
      })()}
      <div className="tw:mt-2 tw:flex tw:flex-col tw:gap-2">
        {editing?.kind === 'new' ? (
          <WorkAssignmentEditRow
            name={editing.name}
            error={nameError}
            disabled={isSaving}
            onNameChange={(name) => {
              setNameError(null);
              setEditing({ kind: 'new', name });
            }}
            onConfirm={confirmEdit}
            onCancel={cancelEdit}
          />
        ) : null}
        {showEmpty ? (
          <div className={ICS204_MUTED}>No work assignments</div>
        ) : (
          rows.map((wa) =>
            editingExistingId === wa.id && editing?.kind === 'existing' ? (
              <WorkAssignmentEditRow
                key={wa.id}
                name={editing.name}
                error={nameError}
                disabled={isSaving}
                onNameChange={(name) => {
                  setNameError(null);
                  setEditing({ kind: 'existing', id: wa.id, name });
                }}
                onConfirm={confirmEdit}
                onCancel={cancelEdit}
              />
            ) : (
              <div
                key={wa.id}
                className={cn(ICS204_SUBCARD, canEdit && 'tw:cursor-pointer')}
                data-testid="ics204-work-assignment-row"
                onClick={() => startEdit(wa)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    startEdit(wa);
                  }
                }}
                role={canEdit ? 'button' : undefined}
                tabIndex={canEdit ? 0 : undefined}
              >
                <div className="tw:flex tw:items-start tw:gap-2 tw:p-2">
                  <div
                    className="tw:min-w-0 tw:flex-1 tw:text-foreground"
                    data-testid="ics204-work-assignment-name"
                  >
                    <Ics204RichTextView value={wa.work_assignment ?? ''} />
                  </div>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Delete work assignment"
                      className={cn(ICS204_DELETE_BTN, 'tw:shrink-0')}
                      data-testid="ics204-edit-delete-work-assignment"
                      disabled={isSaving}
                      onClick={(e) => {
                        e.stopPropagation();
                        void archiveRow(wa);
                      }}
                    >
                      <Trash2 className="tw:h-4 tw:w-4" />
                    </Button>
                  ) : null}
                </div>
              </div>
            )
          )
        )}
      </div>
    </div>
  );
};

const WorkAssignmentEditRow = ({
  name,
  error,
  disabled,
  onNameChange,
  onConfirm,
  onCancel,
}: {
  name: string;
  error: string | null;
  disabled: boolean;
  onNameChange: (name: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div
    className="tw:mb-2 tw:flex tw:items-start tw:gap-2 tw:border-b tw:border-[var(--border-primary)] tw:pb-2"
    data-testid="ics204-edit-work-assignment-row"
  >
    <div className="tw:min-w-0 tw:flex-1">
      <div data-testid="ics204-edit-work-assignment-name">
        <TiptapRichTextEditor
          value={name}
          onChange={onNameChange}
          disabled={disabled}
          showToolbar={!disabled}
          minHeight="80px"
          placeholder="Work assignment"
          className={ICS204_TIPTAP}
        />
      </div>
      {error ? (
        <div className="tw:mt-1 tw:text-xs tw:text-destructive">{error}</div>
      ) : null}
    </div>
    <div className="tw:ml-auto tw:flex tw:shrink-0 tw:items-center tw:gap-1">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Confirm work assignment"
        className={ICS204_ICON_BTN}
        data-testid="ics204-edit-confirm-work-assignment"
        disabled={disabled}
        onClick={onConfirm}
      >
        <Check className="tw:h-4 tw:w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Cancel work assignment"
        className={ICS204_DELETE_BTN}
        data-testid="ics204-edit-cancel-work-assignment"
        disabled={disabled}
        onClick={onCancel}
      >
        <X className="tw:h-4 tw:w-4" />
      </Button>
    </div>
  </div>
);

export default WorkAssignmentsSection;
